import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { WorkspaceStore } from '../../../services/stores/workspace.store';
import { WorkspaceService } from '../../../services/workspace.service';
import { BaseComponent } from '../../base/base.component';
import { IWorkspaceInstanceModel } from '../../../models/workspace/workspace-instance.model';
import { BlueprintStore } from '../../../services/stores/blueprint.store';

// selectedWorkspace/blueprintLoaded/allBluePrints/patchedEdgeInstance can each fire their own
// reload within the same project load (e.g. allBluePrints re-emits on every blueprint mutation
// anywhere in the app); debouncing collapses that burst into a single request instead of one per source.
export const RELOAD_DEBOUNCE_MS = 100;

@Component({
  selector: 'app-status-percentage',
  templateUrl: './status-percentage.component.html',
  styleUrls: ['./status-percentage.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StatusPercentageComponent extends BaseComponent implements OnInit, OnDestroy {
  public percentage: string = '';

  private selectedWorkspace: IWorkspaceInstanceModel = {} as IWorkspaceInstanceModel;

  private reloadTrigger = new Subject<void>();

  constructor(
    private workspaceStore: WorkspaceStore,
    private workspaceService: WorkspaceService,
    private blueprintStore: BlueprintStore,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribers.completionPercentage = this.reloadTrigger
      .pipe(
        debounceTime(RELOAD_DEBOUNCE_MS),
        switchMap(() => {
          const workspaceId = this.selectedWorkspace?.id;
          if (!workspaceId) {
            return of(null);
          }

          const nodeIds = (this.selectedWorkspace.nodeInstances || []).map((n) => n?.id).filter((id): id is string => !!id);

          // getWorkspaceCompletionPercentage can throw synchronously (e.g. an expired-token auth
          // header build) before it ever returns an Observable, which would otherwise escape past
          // the .pipe(catchError) below and error the switchMap itself - permanently tearing down
          // this subscription so no later reloadTrigger emission is ever seen again.
          try {
            return this.workspaceService
              .getWorkspaceCompletionPercentage(workspaceId, this.selectedWorkspace.projectId, this.workspaceStore.IsRollUp, nodeIds)
              .pipe(catchError(() => of(null)));
          } catch {
            return of(null);
          }
        }),
      )
      .subscribe({
        next: (percentage) => {
          this.percentage = percentage === null ? '' : percentage + '%';
          this.cdr.detectChanges();
        },
        error: () => {
          this.percentage = '';
          this.cdr.detectChanges();
        },
      });

    this.subscribers.selectedWorkspace = this.workspaceStore.$selectedWorkspaceInstance.subscribe((data) => {
      if (data?.bluePrintId) {
        this.blueprintStore.ensureBlueprintLoaded(data.bluePrintId);
      }

      this.initializeWorkspaceData(data);
    });

    this.subscribers.blueprintLoaded = this.blueprintStore.$selectedBluePrint.subscribe((blueprint) => {
      const workspace = this.workspaceStore.selectedWorkspaceInstance;
      if (!workspace?.bluePrintId || !blueprint || blueprint.id !== workspace.bluePrintId) {
        return;
      }

      this.initializeWorkspaceData(workspace);
    });

    this.subscribers.allBlueprints = this.blueprintStore.$allBluePrints.subscribe(() => {
      const workspace = this.workspaceStore.selectedWorkspaceInstance;
      if (!workspace?.bluePrintId) {
        return;
      }

      this.initializeWorkspaceData(workspace);
    });

    this.subscribers.patchedEdgeInstance = this.workspaceStore.$patchedEdgeInstance.subscribe((res: any) => {
      if (res) {
        this.loadNodesForWorkspace();
      }
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  private initializeWorkspaceData(workspace: IWorkspaceInstanceModel | null | undefined): void {
    if (!workspace?.bluePrintId) {
      return;
    }

    const blueprint = this.blueprintStore.getBlueprintByIdFromCache(workspace.bluePrintId);
    if (!this.blueprintStore.isBlueprintHydrated(blueprint)) {
      this.blueprintStore.ensureBlueprintLoaded(workspace.bluePrintId);
      return;
    }

    this.selectedWorkspace = workspace;
    this.loadNodesForWorkspace();
  }

  private loadNodesForWorkspace(): void {
    if (!this.selectedWorkspace?.id) {
      return;
    }

    this.reloadTrigger.next();
  }
}
